import { Button, Divider, Drawer as AntdDrawer, Space, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import useLocalStore from 'hooks/useLocalStore';
import { useEditFormStore } from 'stores/local';
import { DictionarySettingsStore } from 'stores/models/fieldsSettings';

import {
  DefaultValueRenderer,
  DictionaryFooterControl,
  DictionaryHead,
  DictionaryTable,
} from './components';

const DictionarySettingsDrawer: React.FC = () => {
  const { t } = useTranslation();
  const { editFormStore, editFormStoreMediator } = useEditFormStore();

  const dictionarySettingsStore = useLocalStore(
    () =>
      new DictionarySettingsStore({
        mediator: editFormStoreMediator,
        getTableRowEntityByCatalogId:
          editFormStore.dataModel.getTableRowEntityByCatalogId,
        handleSaveForm: editFormStore.updateForm,
      })
  );

  const {
    dictionaryUpdateRequest,
    fetchDictionaryById,
    resetToInitial,
    handleSave,
    buttonDisabled,
    drawerController,
    id,
    allValuesUnique,
  } = dictionarySettingsStore;

  React.useEffect(() => {
    if (id && dictionarySettingsStore.drawerController.opened) {
      fetchDictionaryById();
    }
  }, [
    fetchDictionaryById,
    id,
    dictionarySettingsStore.drawerController.opened,
  ]);

  const isLoadingSave =
    dictionaryUpdateRequest.loading ||
    editFormStore.formStore.formUpdateRequest.loading;

  const saveButton = React.useMemo(() => {
    return (
      <Button
        size="large"
        onClick={handleSave}
        disabled={buttonDisabled}
        loading={isLoadingSave}
      >
        {t('common.save')}
      </Button>
    );
  }, [buttonDisabled, handleSave, isLoadingSave, t]);

  const saveButtonNode = React.useMemo(() => {
    if (!allValuesUnique) {
      return (
        <Tooltip
          title={t('common.allValuesMustBeUnique')}
          placement="bottom"
        >
          {saveButton}
        </Tooltip>
      );
    }

    return saveButton;
  }, [allValuesUnique, saveButton, t]);

  return (
    <AntdDrawer
      placement="right"
      closable
      mask={false}
      destroyOnClose
      forceRender
      open={drawerController.opened}
      onClose={drawerController.close}
      title={<DictionaryHead store={dictionarySettingsStore} />}
      size="large"
      extra={
        <Space direction="horizontal" size="small">
          <Button
            type="default"
            size="large"
            onClick={resetToInitial}
            disabled={buttonDisabled}
            loading={dictionaryUpdateRequest.loading}
          >
            {t('common.cancel')}
          </Button>
          {saveButtonNode}
        </Space>
      }
    >
      <DefaultValueRenderer store={dictionarySettingsStore} />
      <Divider />
      <DictionaryTable store={dictionarySettingsStore} />
      <DictionaryFooterControl store={dictionarySettingsStore} />
    </AntdDrawer>
  );
};

export default observer(DictionarySettingsDrawer);
